import { ActivityRowView, ActivityTotals } from '@app/areas/dashboard/models/activity-report.model';
import { DashboardChartData } from '@app/areas/dashboard/models/chart.model';
import { ColorsEnum } from '@app/areas/dashboard/enums/colors.enum';
import { currency, integer, percent } from './format.util';

/** A single stat tile in the overview panel. */
export interface OverviewPanelData {
  header: string;
  headerColor?: string;
  subHeader: string;
  subHeaderColor?: string;
  body: string;
  bodyColor?: string;
  subBody?: string;
  footer?: string;
  footerColor?: string;
  subFooter?: string;
}

/** Four stat tiles derived from the totals rollup. */
export function buildOverviewTiles(totals: ActivityTotals): OverviewPanelData[] {
  return [
    {
      header: 'OVERALL',
      subHeader: `${integer(totals.overall.transactions)} transactions`,
      body: currency(totals.overall.spend),
      bodyColor: ColorsEnum.LIGHT_BLUE,
      footer: `${currency(totals.overall.avgCustomer)} / customer`,
      subFooter: `${currency(totals.overall.avgTxn)} / transaction`,
    },
    {
      header: 'MEMBERS',
      subHeader: `${percent(totals.members.pctOfTxns)} of transactions`,
      body: currency(totals.members.spend),
      bodyColor: ColorsEnum.MOSSY_GREEN,
      footer: `${integer(totals.members.members)} members`,
      subFooter: `${currency(totals.members.avgMember)} / member`,
    },
    {
      header: 'NON-MEMBERS',
      subHeader: `${integer(totals.nonMembers.transactions)} transactions`,
      body: currency(totals.nonMembers.spend),
      bodyColor: ColorsEnum.BLUE_GREY,
      footer: `${integer(totals.nonMembers.nonMembers)} non-members`,
      subFooter: `${currency(totals.nonMembers.avgNonMember)} / non-member`,
    },
    {
      header: 'MEMBER SHARE',
      subHeader: 'of customers',
      body: percent(totals.members.pctOfCustomers),
      bodyColor: ColorsEnum.DOC_PURPLE,
      footer: `${integer(totals.members.members)} of ${integer(totals.overall.customers)}`,
      subFooter: `${percent(totals.members.pctOfTxns)} of transactions`,
    },
  ];
}

/**
 * Stacked bar chart of Member vs Non-Member spend per location (highest total
 * first). The coalition report is aggregated over the whole window, so there is no
 * coalition time series to plot — this uses the available per-location data.
 */
export function buildLocationSpendChart(rows: ActivityRowView[]): DashboardChartData {
  const sorted = [...rows].sort((a, b) => b.amountTotal - a.amountTotal);

  return {
    labels: sorted.map((r) => r.merchantName),
    datasets: [
      {
        label: 'Member',
        data: sorted.map((r) => round2(r.amountTotalMember)),
        backgroundColor: ColorsEnum.MOSSY_GREEN,
        stack: 'spend',
      },
      {
        label: 'Non-Member',
        data: sorted.map((r) => round2(r.amountTotalNonMember)),
        backgroundColor: ColorsEnum.BLUE_GREY,
        stack: 'spend',
      },
    ],
  };
}

function round2(n: number): number {
  return Math.round((n ?? 0) * 100) / 100;
}
